import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly baseUrl = '';

  constructor(private http: HttpClient) {}

  get<T>(
    endpoint: string,
    queryParams?: HttpParams,
    pathParams?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Observable<T> {
    const url = this.buildUrl(endpoint, pathParams);
    return this.http.get<T>(`${this.baseUrl}${url}`, {
      headers: this.mergeHeaders(headers),
      params: queryParams,
    });
  }

  post<T extends { [key: string]: any }, R>(
    endpoint: string,
    payload: T,
    pathParams?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Observable<R> {
    const url = this.buildUrl(endpoint, pathParams);

    let sanitizedPayload = payload;
    // Strip out sensitive info before Api call
    if (payload && Object.prototype.hasOwnProperty.call(payload, 'workpaperPassword')) {
      const { workpaperPassword, ...rest } = payload;
      sanitizedPayload = rest as T;
    }
    return this.http.post<R>(`${this.baseUrl}${url}`, sanitizedPayload, {
      headers: this.mergeHeaders(headers),
    });
  }

  put<T, R>(
    endpoint: string,
    payload: T,
    pathParams?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Observable<R> {
    const url = this.buildUrl(endpoint, pathParams);
    return this.http.put<R>(`${this.baseUrl}${url}`, payload, {
      headers: this.mergeHeaders(headers),
    });
  }

  patch<T, R>(
    endpoint: string,
    payload: T,
    pathParams?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Observable<R> {
    const url = this.buildUrl(endpoint, pathParams);
    return this.http.patch<R>(`${this.baseUrl}${url}`, payload, {
      headers: this.mergeHeaders(headers),
    });
  }

  delete<R>(
    endpoint: string,
    pathParams?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Observable<R> {
    const url = this.buildUrl(endpoint, pathParams);
    return this.http.delete<R>(`${this.baseUrl}${url}`, {
      headers: this.mergeHeaders(headers),
    });
  }

  getBlob(
    endpoint: string,
    queryParams?: HttpParams,
    pathParams?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Observable<Blob> {
    const url = this.buildUrl(endpoint, pathParams);
  
    const customHeaders = this.mergeBlobHeaders(headers); // skip content-type
    return this.http.get(`${this.baseUrl}${url}`, {
      headers: customHeaders,
      params: queryParams,
      responseType: 'blob'
    });
  }

  private mergeHeaders(
    additionalHeaders?: Record<string, string>
  ): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (additionalHeaders) {
      Object.entries(additionalHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }

    return headers;
  }

  private mergeBlobHeaders(additionalHeaders?: Record<string, string>): HttpHeaders {
    let headers = new HttpHeaders();
  
    // No default 'Content-Type' for blob download
    if (additionalHeaders) {
      Object.entries(additionalHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }
  
    return headers;
  }

  private buildUrl(
    endpoint: string,
    pathParams?: Record<string, string | number>
  ): string {
    if (!pathParams) return endpoint;

    return endpoint.replace(
      /{([a-zA-Z0-9]+)}/g,
      (_, key) => pathParams[key]?.toString() || _
    );
  }
}
